/**
 * SectionEditorDialog
 * Dialog for editing a section's content and settings.
 * Uses tabs to separate Content and Settings editing.
 */

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ImageUploadField } from '@/components/ui/ImageUploadField'
import { ArrayFieldEditor } from './ArrayFieldEditor'
import { SectionSettingsEditor } from './SectionSettingsEditor'
import { SectionSelectField } from './SectionSelectField'
import { VehicleMultiSelectField } from './VehicleMultiSelectField'
import { useUpdateSection } from '@/hooks/use-page-sections'
import { SECTION_REGISTRY, type FieldConfig, type SectionSettings } from '@limo/shared'
import type { PageSection } from '@limo/database'

interface SectionEditorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  section: PageSection | null
  accountId: string
  pageId: string
}

export function SectionEditorDialog({
  open,
  onOpenChange,
  section,
  accountId,
  pageId,
}: SectionEditorDialogProps) {
  const [content, setContent] = useState<Record<string, unknown>>({})
  const [settings, setSettings] = useState<SectionSettings>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [activeTab, setActiveTab] = useState('content')

  const updateSection = useUpdateSection()

  // Get section definition
  const definition = section ? SECTION_REGISTRY[section.section_type] : null

  // Initialize content and settings when dialog opens
  useEffect(() => {
    if (open && section) {
      setContent(section.content as Record<string, unknown>)
      setSettings((section.settings as SectionSettings) || {})
      setHasChanges(false)
      setActiveTab('content')
    }
  }, [open, section])

  // Update a field value
  const updateField = (key: string, value: unknown) => {
    setContent((prev) => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }

  // Update settings
  const updateSettings = (newSettings: SectionSettings) => {
    setSettings(newSettings)
    setHasChanges(true)
  }

  // Handle save
  const handleSave = async () => {
    if (!section) return

    try {
      await updateSection.mutateAsync({
        sectionId: section.id,
        content,
        settings,
      })
      onOpenChange(false)
    } catch (err) {
      // Error handled by hook
      console.error('Failed to save section:', err)
    }
  }

  // Get icon component
  const IconComponent = definition?.icon
    ? (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[
        definition.icon
      ] || LucideIcons.FileText
    : LucideIcons.FileText

  if (!section || !definition) {
    return null
  }

  const hasContentFields = definition.fields.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{definition.name}</DialogTitle>
              <DialogDescription>{definition.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="flex-1 min-h-0 mt-4">
            <div className="h-[400px] overflow-y-auto pr-2">
              <div className="space-y-6 pb-4">
                {!hasContentFields ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>This section has no editable content fields.</p>
                    <p className="text-sm mt-2">
                      Switch to the Settings tab to customize styling.
                    </p>
                  </div>
                ) : (
                  definition.fields.map((field) => {
                    // Handle conditional visibility. Fall back to the watched
                    // field's registry default when its value is absent, so
                    // sections saved before a field was added still evaluate
                    // conditions against the intended default (not undefined).
                    if (field.conditionalOn) {
                      const { key, value } = field.conditionalOn
                      const watchedField = definition.fields.find(
                        (f) => f.key === key
                      )
                      const currentValue =
                        content[key] ?? watchedField?.defaultValue
                      const shouldShow = Array.isArray(value)
                        ? value.includes(currentValue as string)
                        : currentValue === value
                      if (!shouldShow) return null
                    }
                    return (
                      <FieldEditor
                        key={field.key}
                        field={field}
                        value={content[field.key]}
                        onChange={(value) => updateField(field.key, value)}
                        accountId={accountId}
                        pageId={pageId}
                        sectionId={section?.id}
                      />
                    )
                  })
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="flex-1 min-h-0 mt-4">
            <div className="h-[400px] overflow-y-auto pr-2">
              <SectionSettingsEditor
                settings={settings}
                onChange={updateSettings}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateSection.isPending || !hasChanges}
          >
            {updateSection.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Field editor component
interface FieldEditorProps {
  field: FieldConfig
  value: unknown
  onChange: (value: unknown) => void
  accountId: string
  pageId?: string
  sectionId?: string
}

function FieldEditor({ field, value, onChange, accountId, pageId, sectionId }: FieldEditorProps) {
  const id = `field-${field.key}`

  // Helper to get value with type safety
  const getValue = <T,>(defaultValue: T): T => {
    return (value as T) ?? field.defaultValue ?? defaultValue
  }

  switch (field.type) {
    case 'text':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'textarea':
    case 'richtext':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Textarea
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'number':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            type="number"
            value={getValue(0)}
            onChange={(e) => onChange(Number(e.target.value))}
            placeholder={field.placeholder}
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'boolean':
      return (
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor={id}>{field.label}</Label>
            {field.description && (
              <p className="text-xs text-muted-foreground">
                {field.description}
              </p>
            )}
          </div>
          <Switch
            id={id}
            checked={getValue(false)}
            onCheckedChange={onChange}
          />
        </div>
      )

    case 'select':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Select value={getValue('')} onValueChange={onChange}>
            <SelectTrigger id={id}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'image':
      return (
        <ImageUploadField
          label={field.label}
          value={getValue('')}
          onChange={(url) => onChange(url)}
          accountId={accountId}
          placeholder={field.placeholder || 'Enter image URL or upload...'}
          description={field.description}
        />
      )

    case 'icon':
      return (
        <div className="space-y-2">
          <Label htmlFor={id}>{field.label}</Label>
          <Input
            id={id}
            value={getValue('')}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g., Shield, Star, Users"
          />
          <p className="text-xs text-muted-foreground">
            {field.description ||
              'Enter a Lucide icon name (e.g., Shield, Star, Users)'}
          </p>
        </div>
      )

    case 'array':
      return (
        <ArrayFieldEditor
          field={field}
          value={getValue([])}
          onChange={onChange}
          accountId={accountId}
        />
      )

    case 'slider':
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor={id}>{field.label}</Label>
            <span className="text-sm font-medium tabular-nums">
              {getValue(field.min ?? 0)}
            </span>
          </div>
          <Slider
            id={id}
            value={[getValue(field.min ?? 0)]}
            onValueChange={(values) => onChange(values[0])}
            min={field.min ?? 0}
            max={field.max ?? 100}
            step={field.step ?? 1}
            className="w-full"
          />
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
        </div>
      )

    case 'section_select':
      // Section select requires pageId and sectionId
      if (!pageId || !sectionId) {
        return (
          <div className="space-y-2">
            <Label>{field.label}</Label>
            <p className="text-sm text-muted-foreground">
              Section selection is not available in this context.
            </p>
          </div>
        )
      }
      return (
        <SectionSelectField
          value={getValue('')}
          onChange={(sectionId) => onChange(sectionId)}
          pageId={pageId}
          currentSectionId={sectionId}
          label={field.label}
          description={field.description}
        />
      )

    case 'vehicle_select':
      return (
        <VehicleMultiSelectField
          value={getValue<string[]>([])}
          onChange={(ids) => onChange(ids)}
          accountId={accountId}
          label={field.label}
          description={field.description}
        />
      )

    default:
      return (
        <div className="space-y-2">
          <Label>{field.label}</Label>
          <p className="text-sm text-muted-foreground">
            Field type "{field.type}" is not yet supported.
          </p>
        </div>
      )
  }
}
